/**
 * AI Match's two requests (docs/ai-match.md), mirroring src/rai/api/routes/ai_match.py.
 * /me/ai-match is light and polled every minute for the mascot and the home counters;
 * /me/ai-match/feed is the window's content, polled only while the window is open.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { request } from "@/api/client";
import { useSession } from "@/app/session";
import { formatInt } from "@/lib/format";

export type Who = "algo" | "ai";
export type Side = "left" | "right";
export type Market = "index" | "stocks" | "mcx";
export type Mood = "watching" | "happy" | "sad";
export type TabKey = "live" | "scorecard" | "picks" | "gist";
export type MarketFilter = "all" | Market;
export type ModelFilter = "both" | Who;

export interface Counters {
  stocks: number;
  news: number;
  decisions: number;
  paper_trades: number;
}
export interface Summary {
  today: string;
  today_hidden: boolean;
  today_note?: string | null;
  market_open: boolean;
  mood: Mood;
  bubble: string;
  leader?: Who | "level" | null;
  net: Record<Who, number | null>;
  open: Record<Who, number | null>;
  open_net: Record<Who, number | null>;
  /** Today's entries and exits so far: what the mascot's count is worked out from. */
  moves: number;
  as_of: string;
}
export interface Me {
  on: boolean;
  counters?: Counters;
  name?: string;
  position?: Side;
  tabs?: TabKey[];
  gist?: string;
  summary?: Summary;
}
export interface Item {
  id: string;
  kind: "start" | "entry" | "exit" | "skip";
  ts: string;
  who?: Who | null;
  market?: Market | null;
  symbol?: string | null;
  instrument?: string | null;
  side?: "BUY" | "SELL" | null;
  price?: number | null;
  stop?: number | null;
  target?: number | null;
  pnl?: number | null;
  result?: "target" | "stop" | "signal" | "time" | null;
  reason?: string | null;
}
export interface Score {
  net: number;
  closed: number;
  wins: number;
  open: number;
  open_net: number;
  best?: { symbol: string; net: number } | null;
}
export interface Cell {
  ok: boolean;
  value?: number | null;
}
export interface TestTable {
  headline: string;
  day: number;
  sessions: number;
  chosen?: Who | null;
  checks: { key: string; label: string; algo: Cell; ai: Cell }[];
}
export interface Pick {
  symbol: string;
  market: Market;
  algo?: string | null;
  ai?: string | null;
}
export interface Feed {
  summary: Summary;
  items: Item[];
  scorecard?: { today: Record<Who, Score>; test: TestTable } | null;
  picks?: Pick[] | null;
  gist?: { title: string; lines: string[] } | null;
}

export const SUMMARY_MS = 60_000;
export const FEED_MS = 15_000;
const OPEN_KEY = "rai.aiMatch.open";
const SEEN_KEY = "rai.aiMatch.seen";
const SIDE_KEY = "rai.aiMatch.side";

export function useAiMatch(enabled = true) {
  const { user } = useSession();
  return useQuery({
    // the person's id: a view-as tab or another sign-in never gets someone else's answer
    queryKey: ["/me/ai-match", user?.id ?? null],
    queryFn: ({ signal }) => request<Me>("get", "/me/ai-match", { signal }),
    enabled: enabled && !!user,
    refetchInterval: SUMMARY_MS,
    retry: false,
  });
}

export function useAiMatchFeed(open: boolean, market: MarketFilter, model: ModelFilter) {
  const { user } = useSession();
  return useQuery({
    queryKey: ["/me/ai-match/feed", user?.id ?? null, market, model],
    queryFn: ({ signal }) =>
      request<Feed>("get", "/me/ai-match/feed", { query: { market, model }, signal }),
    enabled: open,
    refetchInterval: open ? FEED_MS : false,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** The window's open/closed state, per device. Storage may be blocked: then it just starts closed. */
export function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeOpen(open: boolean): void {
  try {
    localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  } catch {
    // the state simply isn't remembered
  }
}

/** The side this device moved it to; null follows the admin's default. */
export function readSide(): Side | null {
  try {
    const v = localStorage.getItem(SIDE_KEY);
    return v === "left" || v === "right" ? v : null;
  } catch {
    return null;
  }
}

export function writeSide(side: Side): void {
  try {
    localStorage.setItem(SIDE_KEY, side);
  } catch {
    // it lasts until the page reloads
  }
}

/** Entries and exits since this device last had the window open today. */
export function unseenMoves(today: string, moves: number): number {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) ?? "null") as { day: string; moves: number } | null;
    return Math.max(0, moves - (seen?.day === today ? seen.moves : 0));
  } catch {
    return 0;
  }
}

export function markSeen(today: string, moves: number): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify({ day: today, moves }));
  } catch {
    // the count simply isn't remembered
  }
}

/** +₹14,200, −₹3,800 or ₹0, whole rupees. */
export function signedINR(n: number): string {
  const body = `₹${formatInt(Math.abs(Math.round(n)))}`;
  if (Math.round(n) === 0) return body;
  return `${n > 0 ? "+" : "−"}${body}`;
}

/** ₹24,402.50, the price as it is. */
export function priceINR(n: number): string {
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const IST_TIME = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export function timeIST(ts: string): string {
  return IST_TIME.format(new Date(ts));
}
